import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/** Routeur minimal sur l'API History — aucune dépendance (ADR-0003). */

interface RouteContexte {
  chemin: string;
  naviguer: (vers: string) => void;
}

const Ctx = createContext<RouteContexte | null>(null);

export function Router({ children }: { children: ReactNode }): React.ReactElement {
  const [chemin, setChemin] = useState<string>(() => window.location.pathname);

  useEffect(() => {
    const onPop = (): void => setChemin(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const naviguer = useCallback((vers: string): void => {
    if (vers !== window.location.pathname) {
      window.history.pushState(null, "", vers);
      setChemin(window.location.pathname);
      window.scrollTo(0, 0);
    }
  }, []);

  const valeur = useMemo<RouteContexte>(() => ({ chemin, naviguer }), [chemin, naviguer]);
  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useRoute(): RouteContexte {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useRoute hors d'un Router");
  return ctx;
}

export function Lien({
  vers,
  children,
  className,
  style,
}: {
  vers: string;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}): React.ReactElement {
  const { naviguer } = useRoute();
  return (
    <a
      href={vers}
      className={className}
      style={style}
      onClick={(e) => {
        e.preventDefault();
        naviguer(vers);
      }}
    >
      {children}
    </a>
  );
}

export function Redirection({ vers }: { vers: string }): null {
  const { naviguer } = useRoute();
  useEffect(() => { naviguer(vers); }, [naviguer, vers]);
  return null;
}
